import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Accordion, AccordionTab } from 'primereact/accordion';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';
import BaseCheckboxThemeGeneral from '../../components/form/BaseCheckboxThemeGeneral.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import {
    useGetContactDuplicatesQuery,
    useGetContactDuplicateStatusesQuery,
    useMassDeleteContactsMutation,
    useUpdateContactDuplicateStatusMutation
} from '../../api/contact.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { Dialog } from 'primereact/dialog';
import IconCloseFull from '../../components/images/IconCloseFull.jsx';
import { PERMISSION_CONTACTS } from '../../utils/constants.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';

const ContactDuplicates = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { control, watch } = useForm();
    const { permissions: userPermissions } = useCurrentUser();
    const [status, setStatus] = useState('');
    const [confirmMassDeleteGroup, setConfirmMassDeleteGroup] = useState(null);
    const [associatedError, setAssociatedError] = useState('');

    const [statusDialogVisible, setStatusDialogVisible] = useState(false);
    const [dialogGroupIndex, setDialogGroupIndex] = useState(null);
    const [selectedStatusValue, setSelectedStatusValue] = useState('');

    const [scrollToGroupId, setScrollToGroupId] = useState(null);

    const { data: statusesData, isLoading: statusesLoading } = useGetContactDuplicateStatusesQuery();

    const statusOptions = useMemo(() => {
        return (
            statusesData?.response?.contact_duplicate_statuses?.map((item) => ({
                label: item.label,
                value: item.value
            })) || []
        );
    }, [statusesData]);

    const searchByStatus = (value) => {
        const newStatus = typeof value === 'object' && value !== null ? value.value : value;
        setStatus(newStatus);
    };

    const resetStatus = () => {
        setStatus('');
    };

    const requestParams = useMemo(() => ({ status }), [status]);

    const {
        data,
        isLoading: queryLoading,
        refetch
    } = useGetContactDuplicatesQuery(requestParams, {
        refetchOnMountOrArgChange: false
    });

    const duplicates = useMemo(() => data?.response?.duplicates || [], [data]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get('scrollTo');
        if (id) setScrollToGroupId(id);
    }, [location.search]);

    useEffect(() => {
        if (scrollToGroupId && duplicates.length) {
            const el = document.getElementById(`duplicate-group-${scrollToGroupId}`);
            if (el) el.scrollIntoView({ behavior: 'smooth' });
        }
    }, [duplicates, scrollToGroupId]);

    const watchedValues = watch();

    const activeGroup = useMemo(() => {
        let active = null;
        duplicates.forEach((group, groupIndex) => {
            const groupSelected = group.contacts.some(
                (contact) => watchedValues[`group_${groupIndex}_contact_${contact.id}`]
            );
            if (active === null && groupSelected) {
                active = groupIndex;
            }
        });
        return active;
    }, [watchedValues, duplicates]);

    const [massDeleteContacts, { isLoading: isLoadingMassDelete }] = useMassDeleteContactsMutation();
    const [updateContactDuplicateStatus, { isLoading: isUpdatingStatus }] = useUpdateContactDuplicateStatusMutation();

    const submitGroupDeleteContacts = async (groupIndex) => {
        const group = duplicates[groupIndex];
        const ids = group.contacts
            .filter((contact) => watchedValues[`group_${groupIndex}_contact_${contact.id}`])
            .map((contact) => contact.id);
        try {
            const response = await massDeleteContacts({ ids });
            if (response.data?.success) {
                setConfirmMassDeleteGroup(null);
            } else {
                console.error(response.data?.message);
            }
        } catch (error) {
            console.error('Failed to delete contacts:', error);
        }
    };

    const duplicateStatusMapping = {
        new: { text: 'New', color: 'text-theme1-info' },
        'not-duplicate': { text: 'Not Duplicate', color: 'text-theme1-gold' }
    };

    const openStatusDialog = (groupIndex) => {
        const group = duplicates[groupIndex];
        if (group.status !== 'new') return;
        setDialogGroupIndex(groupIndex);
        setSelectedStatusValue(group.status);
        setStatusDialogVisible(true);
    };

    const hideStatusDialog = () => setStatusDialogVisible(false);

    const confirmStatusChange = async () => {
        const group = duplicates[dialogGroupIndex];
        try {
            await updateContactDuplicateStatus({
                id: group.id,
                data: { status: selectedStatusValue }
            }).unwrap();
            refetch();
        } catch (error) {
            console.error('Failed to update duplicate status:', error);
        }
        hideStatusDialog();
    };

    const handleMerge = (contacts, index, id) => {
        setAssociatedError('');

        const selectedContacts = contacts.filter((contact) => {
            const fieldName = `group_${index}_contact_${contact.id}`;
            return watchedValues[fieldName];
        });

        const associatedCount = selectedContacts.filter((contact) => contact.associated_user_id != null).length;

        if (associatedCount > 1) {
            setAssociatedError('Cannot merge contacts associated with users amongst each other!');
        } else {
            const selectedContactIds = selectedContacts.map((contact) => contact.id).join(',');
            navigate(`/contacts/duplicates/${id}?contactIds=${selectedContactIds}`);
        }
    };

    const handleDelete = (index) => {
        const group = duplicates[index];

        const selectedContacts = group.contacts.filter((contact) => {
            const fieldName = `group_${index}_contact_${contact.id}`;
            return watchedValues[fieldName];
        });

        const hasAssociatedUser = selectedContacts.some((contact) => contact.associated_user_id != null);

        if (hasAssociatedUser) {
            setAssociatedError('Cannot delete contacts associated with users!');
        } else {
            setConfirmMassDeleteGroup(index);
        }
    };

    return (
        <AuthLayout title='Contacts Duplicates' backRoute='/contacts/list'>
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-6'>
                <div className='flex justify-between'>
                    <div className='flex-grow'>
                        <BaseDropdown
                            name='status'
                            placeholder='Select Status'
                            value={status}
                            setValue={searchByStatus}
                            options={statusOptions}
                            disabled={statusesLoading || queryLoading}
                        />
                    </div>
                    <div className='text-theme1-primary2 cursor-pointer ml-4 mt-2' onClick={resetStatus}>
                        Reset
                    </div>
                </div>
            </div>

            <div className='px-8 max-w-3xl w-full mx-auto mb-6'>
                {associatedError && <div className='p-0 mt-2 text-theme1-brightRed font-bold'>{associatedError}</div>}
                {queryLoading ? (
                    <ThreeDotsLoader />
                ) : duplicates.length > 0 ? (
                    duplicates.map((group, groupIndex) => {
                        const selectedCount = group.contacts.reduce((count, contact) => {
                            const fieldName = `group_${groupIndex}_contact_${contact.id}`;
                            return count + (watchedValues[fieldName] ? 1 : 0);
                        }, 0);
                        const groupCount = group.contacts.length;

                        const showMerge = groupCount > 1 && selectedCount >= 2;
                        const showDelete = selectedCount >= 1 && selectedCount < groupCount;

                        return (
                            <div id={`duplicate-group-${group.id}`} key={`group-${groupIndex}`} className='mb-6'>
                                <div className='flex justify-between items-center border-b border-theme1-text1 pb-2'>
                                    <div className='text-lg font-bold'>{groupIndex + 1}</div>
                                    {!!userPermissions[PERMISSION_CONTACTS]?.update &&
                                        duplicateStatusMapping[group.status] && (
                                            <span
                                                className={duplicateStatusMapping[group.status].color}
                                                onClick={() => openStatusDialog(groupIndex)}
                                                style={{ cursor: group.status === 'new' ? 'pointer' : 'default' }}
                                            >
                                                {duplicateStatusMapping[group.status].text}
                                            </span>
                                        )}
                                </div>

                                <Accordion
                                    className='custom-accordion custom-accordion-nobold custom-accordion-borderd'
                                    multiple
                                    activeIndex={[]}
                                >
                                    {group.contacts.map((contact) => {
                                        const fieldName = `group_${groupIndex}_contact_${contact.id}`;
                                        const disabledCheckbox = activeGroup !== null && activeGroup !== groupIndex;

                                        const headerTemplate = (
                                            <div className='flex items-center'>
                                                <div onClick={(e) => e.stopPropagation()}>
                                                    <BaseCheckboxThemeGeneral
                                                        name={fieldName}
                                                        control={control}
                                                        label={`${contact.first_name} ${contact.last_name}`}
                                                        className='mr-2'
                                                        disabled={disabledCheckbox}
                                                        inputClassName={`${disabledCheckbox ? 'border-theme1-disabled' : ''}`}
                                                        labelClassName={`text-theme1-primary2 ${disabledCheckbox ? 'text-theme1-disabled' : ''}`}
                                                    />
                                                </div>
                                            </div>
                                        );

                                        return (
                                            <AccordionTab key={contact.id} header={headerTemplate}>
                                                <div className='pb-4 -mt-1'>
                                                    <div>
                                                        <strong>Email:</strong>{' '}
                                                        {contact.email ? (
                                                            contact.email
                                                        ) : (
                                                            <span className='text-theme1-brightRed'>
                                                                No email assigned
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <strong>Phone number:</strong>{' '}
                                                        {contact.phone ? (
                                                            contact.phone
                                                        ) : (
                                                            <span className='text-theme1-brightRed'>
                                                                No phone number assigned
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className='mt-2'>
                                                        <Link
                                                            to={`/contacts/view/${contact.id}`}
                                                            className='text-theme1-info'
                                                        >
                                                            Go to contact
                                                        </Link>
                                                    </div>
                                                </div>
                                            </AccordionTab>
                                        );
                                    })}
                                </Accordion>
                                <div className='mt-3 flex justify-between'>
                                    {!!userPermissions[PERMISSION_CONTACTS]?.update && showMerge && (
                                        <button
                                            className='p-0 text-theme1-success'
                                            onClick={() => handleMerge(group.contacts, groupIndex, group.id)}
                                        >
                                            Merge
                                        </button>
                                    )}

                                    {!!userPermissions[PERMISSION_CONTACTS]?.delete && showDelete && (
                                        <button
                                            className='p-0 text-theme1-brightRed'
                                            onClick={() => handleDelete(groupIndex)}
                                        >
                                            Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p>No duplicates found.</p>
                )}
            </div>

            {confirmMassDeleteGroup !== null && (
                <div className='z-20 fixed left-0 bottom-0 w-full bg-auth bg-center bg-cover rounded-t-4xl p-6'>
                    <div className='text-white font-semibold text-lg text-center'>
                        Are you sure you want to delete{' '}
                        {duplicates[confirmMassDeleteGroup].contacts.reduce((count, contact) => {
                            const fieldName = `group_${confirmMassDeleteGroup}_contact_${contact.id}`;
                            return count + (watchedValues[fieldName] ? 1 : 0);
                        }, 0)}{' '}
                        selected contact
                        {duplicates[confirmMassDeleteGroup].contacts.reduce((count, contact) => {
                            const fieldName = `group_${confirmMassDeleteGroup}_contact_${contact.id}`;
                            return count + (watchedValues[fieldName] ? 1 : 0);
                        }, 0) > 1
                            ? 's'
                            : ''}
                        ?
                    </div>
                    <div className='flex justify-between items-center gap-4 mt-6'>
                        <BaseButton
                            className='btn-red-outline !text-theme1-white !border-theme1-white'
                            textValue={'Cancel'}
                            onClick={() => setConfirmMassDeleteGroup(null)}
                        />
                        <BaseButton
                            className='btn-red-outline !text-theme1-white !border-theme1-white'
                            textValue={'Delete'}
                            onClick={() => submitGroupDeleteContacts(confirmMassDeleteGroup)}
                            disabled={isLoadingMassDelete}
                        />
                    </div>
                </div>
            )}

            <Dialog
                className='rounded-4xl shadow-deep-red-shadow'
                visible={statusDialogVisible}
                style={{ width: '400px' }}
                breakpoints={{ '420px': '320px' }}
                onHide={hideStatusDialog}
                content={() => (
                    <div className='flex flex-col justify-center items-center px-6 py-8 pt-12 bg-white rounded-4xl'>
                        <button onClick={hideStatusDialog} className='absolute top-6 right-6'>
                            <IconCloseFull className='fill-theme1-text5' />
                        </button>
                        <div className='text-theme1-primary text-[22px] mb-[30px] text-center leading-tight font-bold'>
                            Change duplicate status for group {dialogGroupIndex + 1}
                        </div>
                        <div className='w-full mb-9'>
                            <BaseDropdown
                                name='status'
                                placeholder='Select Status'
                                value={selectedStatusValue}
                                setValue={setSelectedStatusValue}
                                options={statusOptions}
                                disabled={statusesLoading || queryLoading}
                            />
                        </div>
                        <div className='flex justify-between gap-4 w-full'>
                            <BaseButton
                                className='btn-red-outline w-[47%] flex-shrink-0'
                                textValue='Cancel'
                                onClick={hideStatusDialog}
                            />
                            <BaseButton
                                className={`text-white w-[47%] flex-shrink-0 ${!selectedStatusValue || selectedStatusValue === duplicates[dialogGroupIndex]?.status ? 'bg-theme1-pale' : 'bg-theme1-primary'}`}
                                textValue='Submit'
                                onClick={confirmStatusChange}
                                disabled={
                                    !selectedStatusValue ||
                                    selectedStatusValue === duplicates[dialogGroupIndex]?.status ||
                                    isUpdatingStatus
                                }
                            />
                        </div>
                    </div>
                )}
            ></Dialog>
        </AuthLayout>
    );
};

export default ContactDuplicates;
